import React, { useState, useEffect } from "react";

import { Toast } from 'primereact/toast';
import { Ripple } from "primereact/ripple";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputNumberChangeEvent } from 'primereact/inputnumber'

import TextInput from "../../../../../Components/TextInput";
import NumberInput from "../../../../../Components/NumberInput";
import { checkNumberForProgramme, checkText } from "../../../../../Utils/Validator";
import { programCreateApi, programUpdateApi } from "../../../../../Api/Project/DefaultServices/Program/ProgramApi";
import { Programme } from "../../../../../Controllers/Types/Project/DefaultService/ProgramInterface";

interface ProgramModalProps {
    isOpen: boolean;
    dataState: 'Add' | 'Edit';
    type: 'General' | 'Floor';
    onFetchData: () => void;
    onClose: () => void;
    onCancel: () => void;
    toast: React.RefObject<Toast>;
    serviceId?: number | null;
    programme?: Programme | null;
}

const ProgramModal: React.FC<ProgramModalProps> = ({ isOpen, dataState, type, onFetchData, onClose, onCancel, toast, serviceId, programme }) => {
    const [loading, setLoading] = useState<boolean>(false);

    const [programmeName, setProgrammeName] = useState<string>('');
    const [basementCount, setBasementCount] = useState<number>(0);
    const [floorCount, setFloorCount] = useState<number>(0);
    const [programeUpdateHasChanges, setProgrameUpdateHasChanges] = useState<boolean>(false);

    /* Error */
    const [programmeNameError, setProgrammeNameError] = useState<string>('');
    const [basementCountError, setBasementCountError] = useState<string>('');
    const [floorCountError, setFloorCountError] = useState<string>('');

    useEffect(() => {
        setDataValue();
    }, [dataState, programme]);

    function setDataValue() {
        if (dataState === 'Add') {
            handleClearProgramFields();
        } else {
            setProgrammeName(programme?.program_name || '');
            setBasementCount(programme?.no_of_basement || 0);
            setFloorCount(programme?.no_of_floor || 0);
        }
    }

    const handleClearProgramFields = () => {
        setProgrammeName('');
        setBasementCount(0);
        setFloorCount(0);
    }

    const handleClearProgramErrors = () => {
        setProgrammeNameError('');
        setBasementCountError('');
        setFloorCountError('');
    }

    const handleCloseModal = () => {
        onClose();
        handleClearProgramFields();
        handleClearProgramErrors();
    }

    const handleExit = () => {
        onCancel();
        handleClearProgramFields();
    }

    useEffect(() => {
        checkText(programmeName, false, setProgrammeNameError);
    }, [programmeName]);

    useEffect(() => {
        checkNumberForProgramme(basementCount, false, setBasementCountError);
    }, [basementCount]);

    useEffect(() => {
        checkNumberForProgramme(floorCount, false, setFloorCountError);
    }, [floorCount]);

    function checkCredentials(): boolean {
        const isProgrammeNameValid = checkText(programmeName, true, setProgrammeNameError);
        const isBasementCountValid = checkNumberForProgramme(basementCount, (type === 'Floor' ? true : false), setBasementCountError);
        const isFloorCountValid = checkNumberForProgramme(floorCount, (type === 'Floor' ? true : false), setFloorCountError);

        return isProgrammeNameValid && isBasementCountValid && isFloorCountValid;
    }

    const handleCreateProgramme = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentials()) {
            setLoading(true);
            const data = await programCreateApi(
                Number(serviceId),
                "Custom",
                type,
                programmeName,
                basementCount,
                floorCount
            );
            setLoading(false);

            if (data.statusCode === 200) {
                onFetchData();
                handleCloseModal();

                if (toast.current) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Success",
                        detail: `Program detail added successfully.`,
                        life: 3000,
                    });
                }
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error creating program detail.',
                        life: 3000
                    });
                }
            }
        }
    }

    const handleUpdateProgramme = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentials()) {
            setLoading(true);
            const data = await programUpdateApi(
                programme?.project_program_id!,
                programmeName,
                basementCount,
                floorCount
            );

            setLoading(false);

            if (data.statusCode === 200) {
                onFetchData();
                handleCloseModal();

                if (toast.current) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Success",
                        detail: `Program detail updated successfully.`,
                        life: 3000,
                    });
                }

            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error creating program detail.',
                        life: 3000
                    });
                }
            }
        }
    }

    /* Modal header */
    const modalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState}&nbsp;program detail
            </h5>
            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleExit}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    /* Modal footer */
    const modalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleExit}
            />

            <Button
                label={loading ? 'Processing' : dataState === 'Add' ? 'Save' : 'Update'}
                onClick={dataState === 'Add' ? handleCreateProgramme : handleUpdateProgramme}
                loading={loading}
                className="custom_btn primary"
                disabled={dataState === 'Edit' && !programeUpdateHasChanges}
            />
        </div>
    );

    const handleBasementCountChange = (e: InputNumberChangeEvent) => {
        setBasementCount(e.value ?? 0);
    };

    const handleFloorCountChange = (e: InputNumberChangeEvent) => {
        setFloorCount(e.value ?? 0);
    };

    useEffect(() => {
        if (programme) {
            if (programmeName !== programme?.program_name) {
                setProgrameUpdateHasChanges(true);
            } else if (type === 'Floor' && (basementCount !== null && basementCount) !== programme?.no_of_basement) {
                setProgrameUpdateHasChanges(true);
            } else if (type === 'Floor' && (floorCount !== null && floorCount) !== programme?.no_of_floor) {
                setProgrameUpdateHasChanges(true);
            } else {
                setProgrameUpdateHasChanges(false);
            }
        }
    }, [programme, programmeName, basementCount, floorCount, type]);

    return (
        <Dialog
            visible={isOpen}
            header={modalHeader}
            footer={modalFooter}
            headerClassName="custom_modal_header"
            className="custom_modal_dialog modal_dialog_sm"
            onHide={handleExit}
            dismissableMask
        >
            <div className="custom_modal_body">
                <div className="p-1 p-sm-4 pt-sm-3 pb-sm-0 w-100">
                    <div className="row">
                        {/* Program name */}
                        <div className="col-12">
                            <TextInput
                                id="programmeName"
                                label="Program name"
                                labelHtmlFor="programmeName"
                                required={true}
                                value={programmeName}
                                placeholder="Program name"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProgrammeName(e.target.value)}
                                error={programmeNameError}
                            />
                        </div>

                        {type === 'Floor' && (
                            <React.Fragment>
                                {/* No. of basement */}
                                <div className="col-12 col-sm-6">
                                    <NumberInput
                                        inputId="basementCount"
                                        label="No. of basement(s)"
                                        labelHtmlFor="basementCount"
                                        placeholder="No. of basement(s)"
                                        onChange={handleBasementCountChange}
                                        value={basementCount}
                                        required={false}
                                        min={0}
                                        error={basementCountError}
                                    />
                                </div>

                                {/* No. of floor */}
                                <div className="col-12 col-sm-6">
                                    <NumberInput
                                        inputId="floorCount"
                                        label="No. of floor(s)"
                                        labelHtmlFor="floorCount"
                                        placeholder="No. of floor(s)"
                                        onChange={handleFloorCountChange}
                                        value={floorCount}
                                        required={true}
                                        min={0}
                                        error={floorCountError}
                                    />
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                </div>
            </div>
        </Dialog >
    )
}

export default ProgramModal;